import React from "react";
import BannerImg from "../../assets/images/banner/it-consulting.png";
import EnterpriseServicePage from "../../components/services/enterprise-service-page";

const schemas = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Enterprise AI Enablement",
    serviceType: "AI Enablement",
    description:
      "Enterprise AI enablement services for organizations that want to add governed AI to existing software, workflows and integration layers.",
    provider: {
      "@type": "Organization",
      name: "Avani Cimcon Technologies",
      url: "https://www.avanicimcon.com/",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.avanicimcon.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://www.avanicimcon.com/it-consulting-services" },
      {
        "@type": "ListItem",
        position: 3,
        name: "Enterprise AI Enablement",
        item: "https://www.avanicimcon.com/services/enterprise-ai-enablement",
      },
    ],
  },
];

const EnterpriseAIEnablement = () => (
  <EnterpriseServicePage
    title="Enterprise AI Enablement for Existing Systems, Workflows & Integration | Avani Cimcon"
    description="Enterprise AI enablement from Avani Cimcon layers intelligence onto the systems you already trust, with governance, integration and modernization discipline."
    keywords="enterprise AI enablement, AI enablement, enterprise AI, AI governance, AI integration, workflow automation, legacy modernization, enterprise software engineering"
    canonical="https://www.avanicimcon.com/services/enterprise-ai-enablement"
    ogImage="https://www.avanicimcon.com/images/banner/it-consulting.png"
    hero={{
      eyebrow: "Enterprise AI Enablement",
      heading: "Enterprise AI enablement for systems that already carry the business",
      lead:
        "AI has the most value when it can improve the software, workflows and data layers organizations already depend on. This page focuses on that practical layer.",
      tags: ["AI governance", "Workflow automation", "Decision support", "Integration"],
      primaryCta: { label: "Talk to Us", href: "/contact-us" },
      secondaryCta: { label: "See the AI hub", href: "/enterprise-ai" },
      image: BannerImg,
      panelKicker: "AI as a governed layer.",
      panelTitle: "Make AI useful inside real enterprise operations.",
      panelText:
        "The objective is not to add AI everywhere. The objective is to add it where it supports the operating model, improves decisions and respects the systems already in place.",
      stats: [
        { value: "Governed", label: "Human accountable delivery" },
        { value: "Practical", label: "AI on existing workflows" },
        { value: "Safe", label: "Modernization-aligned approach" },
      ],
    }}
    whatItIs={{
      eyebrow: "What It Is",
      title: "A service for adding AI to enterprise systems without starting over",
      lead: "Enterprise AI enablement works best as an extension of modernization and integration.",
      paragraphs: [
        "The company already positions AI as a layer that belongs inside governed enterprise systems. That makes this page a natural bridge between the AI hub and the service cluster.",
        "This service is for organizations that want to improve search, summarization, decision support, routing, forecasting or workflow assistance while keeping the underlying application estate stable.",
      ],
    }}
    whoItIsFor={{
      title: "Who it is for",
      lead: "Buyers who want AI adoption to be practical, incremental and aligned to existing systems.",
      items: [
        "Enterprises with established workflows and data sources",
        "Teams that need AI inside operational software, not outside it",
        "Organizations already planning modernization or integration work",
      ],
    }}
    businessProblems={{
      eyebrow: "Problems Solved",
      title: "The problems enterprise AI can solve when it is well-scoped",
      lead: "Use AI to improve useful work rather than chase novelty.",
      items: [
        {
          title: "Information overload",
          text: "Help teams summarize, classify and surface the most relevant content faster.",
        },
        {
          title: "Manual workflow effort",
          text: "Reduce repetitive work in routing, review, triage and content-heavy operations.",
        },
        {
          title: "Decision latency",
          text: "Make exceptions and patterns easier to see so humans can act with better context.",
        },
      ],
    }}
    scenarios={{
      eyebrow: "Typical Scenarios",
      title: "Typical enterprise AI scenarios",
      lead: "AI use cases that fit existing business and software structures.",
      items: [
        { title: "Document and content assistance", text: "Summarization, classification and retrieval for enterprise content systems." },
        { title: "Workflow augmentation", text: "Suggest next steps, route requests and flag exceptions inside business processes." },
        { title: "Decision support", text: "Surface relevant context and patterns for operational teams and managers." },
        { title: "Integration-aware assistants", text: "Use AI where the data layer and system integration are already in place." },
      ],
    }}
    deliveryApproach={{
      eyebrow: "How We Deliver",
      title: "How Avani Cimcon approaches enterprise AI enablement",
      lead: "Start with the systems, then choose the AI layer that fits them.",
      paragraphs: [
        "The delivery approach begins with the enterprise context: where the data lives, which workflows matter, how governance works and which systems can safely absorb change. That is why the AI service links so closely with modernization and managed services.",
        "The company’s broader engineering experience across travel, healthcare, insurance and legal makes the AI conversation more useful because it stays tied to real operational needs instead of generic claims.",
      ],
      callout: {
        title: "Delivery characteristics",
        items: [
          "AI governed by business context",
          "Integration-first implementation",
          "Incremental adoption rather than big-bang change",
          "Clear handoff into support and modernization",
        ],
      },
    }}
    industries={{
      eyebrow: "Industries Served",
      title: "Enterprise AI opportunities by vertical",
      lead: "The strongest opportunities are in the sectors the company already knows well.",
      items: [
        { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Booking, reservation and service operations." },
        { label: "Healthcare", href: "/healthcare", text: "Patient and clinical workflow support." },
        { label: "Insurance", href: "/insurance", text: "Policy, claims and service operations." },
        { label: "Legal", href: "/legal", text: "Document-heavy and audit-sensitive workflows." },
        { label: "DXchange", href: "/dxchange", text: "Integration and data exchange foundation." },
        { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules and decision logic that AI can complement." },
      ],
    }}
    framework={{
      eyebrow: "Modernization Framework",
      title: "Enterprise AI works best when it follows the modernization framework",
      lead: "AI adoption becomes safer after the estate is assessed, preserved and stabilized.",
      items: [
        { title: "Assess", text: "Find the workflows and data sources that can support AI." },
        { title: "Preserve", text: "Protect the systems and rules already trusted by the business." },
        { title: "Stabilize", text: "Make sure the delivery model can support change reliably." },
        { title: "Enable AI", text: "Add governed intelligence only where it improves outcomes." },
      ],
    }}
    aiOpportunities={{
      eyebrow: "Related AI Opportunities",
      title: "Additional AI paths to explore",
      lead: "The AI service should connect back to the broader enterprise story.",
      items: [
        {
          title: "Enterprise AI Hub",
          text: "The overview page for enterprise AI, decision support and governance.",
          href: "/enterprise-ai",
          linkLabel: "Open the hub",
        },
        {
          title: "Legacy Modernization",
          text: "Use the modernization framework to prepare the estate for AI.",
          href: "/services/legacy-modernization",
          linkLabel: "See the framework",
        },
        {
          title: "Managed Services",
          text: "Keep systems stable while AI features are added and refined.",
          href: "/services/managed-services",
          linkLabel: "See managed services",
        },
      ],
    }}
    relatedProducts={{
      eyebrow: "Related Products",
      title: "Products that support the AI story with real enterprise proof",
      lead: "The product portfolio shows where integration, rules and content systems already exist.",
      items: [
        { label: "DXchange", href: "/dxchange", text: "Integration and enterprise data exchange." },
        { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules management and decision automation." },
        { label: "CMS", href: "/cms", text: "Content operations and structured publishing." },
      ],
    }}
    relatedServices={{
      eyebrow: "Related Services",
      title: "Services that pair well with enterprise AI",
      lead: "AI outcomes improve when the surrounding delivery model is also strong.",
      items: [
        { label: "Managed Services", href: "/services/managed-services", text: "Operational ownership and support." },
        { label: "Dedicated Engineering Teams", href: "/services/dedicated-engineering-teams", text: "Embedded delivery capacity." },
        { label: "Legacy Modernization", href: "/services/legacy-modernization", text: "Preserve and modernize the underlying estate." },
      ],
    }}
    cta={{
      eyebrow: "Talk to Us",
      title: "Want AI that fits your enterprise instead of disrupting it?",
      text: "Let’s map the use case, the data, the governance model and the delivery path together.",
      primary: { label: "Talk to Us", href: "/contact-us" },
      secondary: { label: "Discuss Your Modernization Roadmap", href: "/services/legacy-modernization" },
    }}
    schemas={schemas}
  />
);

export default EnterpriseAIEnablement;
